import React from 'react';
import {
  Box,
  Divider,
  Grid,
  MenuItem,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs, { Dayjs } from '@/config/dayjs';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { useLocalization } from '@/hooks/useLocalization';
import { isCapacitorApp } from '@/utils/isCapacitorApp';
import AddressAutocomplete from '@/components/common/AddressAutocomplete';
import { type UserProfile, type UserDependentProfile } from '@/store/usersApi';
import { formatNumberToCurrency } from '@/utils/currency';
import { digitsOnly, formatPhoneNumber } from '@/utils/phone';
import { outlinedInputRootSx } from '@/constants/dimensions';

const GENDER_OPTIONS = [
  { value: 1, labelKey: 'forms.gender.male' as const },
  { value: 2, labelKey: 'forms.gender.female' as const },
  { value: 3, labelKey: 'forms.gender.other' as const },
];

const EMPLOYMENT_STATUS_OPTIONS = [
  { value: 1, labelKey: 'forms.employmentStatus.fullTime' as const },
  { value: 2, labelKey: 'forms.employmentStatus.partTime' as const },
  { value: 3, labelKey: 'forms.employmentStatus.unemployed' as const },
  { value: 4, labelKey: 'forms.employmentStatus.retired' as const },
];

const MARITAL_STATUS_OPTIONS = [
  { value: 1, labelKey: 'forms.maritalStatusOptions.single' as const },
  { value: 2, labelKey: 'forms.maritalStatusOptions.married' as const },
  { value: 3, labelKey: 'forms.maritalStatusOptions.divorced' as const },
  { value: 4, labelKey: 'forms.maritalStatusOptions.separated' as const },
  { value: 5, labelKey: 'forms.maritalStatusOptions.widowed' as const },
  { value: 6, labelKey: 'forms.maritalStatusOptions.domesticPartner' as const },
];

const sanitizeCurrencyInput = (value: string): number | null => {
  const numeric = Number(value.replace(/[^0-9.-]+/g, ''));
  return Number.isFinite(numeric) ? numeric : null;
};

const toIsoMidnight = (value: Dayjs | string | null): string | null => {
  if (!value) return null;
  const d = typeof value === 'string' ? dayjs(value) : value;
  if (!d.isValid()) return null;
  return `${d.format('YYYY-MM-DD')}T00:00:00`;
};

const toNumberOrNull = (value: number | ''): number | null =>
  value === '' ? null : Number(value);

const buildDependents = (
  count: number,
  existing: UserDependentProfile[],
): UserDependentProfile[] => {
  const rows: UserDependentProfile[] = [];
  for (let idx = 0; idx < count; idx += 1) {
    const candidate = existing[idx];
    rows.push({
      UserDependentId: candidate?.UserDependentId ?? null,
      BirthDay: candidate?.BirthDay ?? null,
    });
  }
  return rows;
};

export interface UseUserProfileFormOptions {
  profile: UserProfile;
  onSubmit: (payload: Partial<UserProfile>) => Promise<void>;
}

export interface UserProfileFormController {
  formik: ReturnType<typeof useFormik<UserProfileFormValues>>;
  dependentsCount: number;
  dependentRows: UserDependentProfile[];
  showDependents: boolean;
  handleDependentsCountChange: (value: string) => void;
  handleDependentBirthdayChange: (index: number, value: Dayjs | null) => void;
  handleNetWorthChange: React.ChangeEventHandler<HTMLInputElement>;
  handlePhoneChange: React.ChangeEventHandler<HTMLInputElement>;
  handleSave: () => Promise<void>;
  canSubmit: boolean;
}

interface UserProfileFormValues {
  firstName: string;
  lastName: string;
  email: string;
  birthday: Dayjs | null;
  gender: number | '';
  netWorth: string;
  phoneNumber: string;
  address: string;
  employmentStatus: number | '';
  employer: string;
  jobTitle: string;
  maritalStatus: number | '';
  spouseFirstName: string;
  spouseLastName: string;
  spouseBirthday: Dayjs | null;
}

export const useUserProfileForm = ({
  profile,
  onSubmit,
}: UseUserProfileFormOptions): UserProfileFormController => {
  const validationSchema = React.useMemo(() => {
    const canEditNameEmail = !!profile.CanEditNameAndEmail;
    return yup.object({
      firstName: canEditNameEmail
        ? yup.string().trim().required('Required')
        : yup.string().trim(),
      lastName: canEditNameEmail
        ? yup.string().trim().required('Required')
        : yup.string().trim(),
      email: canEditNameEmail
        ? yup.string().trim().email('Invalid email').required('Required')
        : yup.string().trim().email('Invalid email'),
      birthday: yup.mixed().nullable(),
      gender: yup.mixed<number | ''>().oneOf(['', 1, 2, 3]),
      netWorth: yup
        .string()
        .test('is-currency', 'Invalid amount', (v) =>
          v ? sanitizeCurrencyInput(v) !== null : true,
        ),
      phoneNumber: yup
        .string()
        .test(
          'phone-format',
          'Invalid phone number',
          (val) => !val || /^\d{10}$/.test(val),
        ),
      address: yup.string(),
      employmentStatus: yup.mixed<number | ''>().oneOf(['', 1, 2, 3, 4]),
      employer: yup.string(),
      jobTitle: yup.string(),
      maritalStatus: yup.mixed<number | ''>().oneOf(['', 1, 2, 3, 4, 5, 6]),
      spouseFirstName: yup.string(),
      spouseLastName: yup.string(),
      spouseBirthday: yup.mixed().nullable(),
    });
  }, [profile.CanEditNameAndEmail]);

  const formik = useFormik<UserProfileFormValues>({
    enableReinitialize: true,
    validateOnMount: true,
    validationSchema,
    initialValues: {
      firstName: profile.FirstName ?? '',
      lastName: profile.LastName ?? '',
      email: profile.EmailAddress ?? '',
      birthday: profile.Birthday ? dayjs(profile.Birthday) : null,
      gender:
        typeof profile.Gender === 'number'
          ? (profile.Gender as number)
          : ('' as number | ''),
      netWorth:
        typeof profile.ApproximateNetWorth === 'number'
          ? formatNumberToCurrency(profile.ApproximateNetWorth, 'USD', 2)
          : '',
      phoneNumber: profile.PhoneNumber ?? '',
      address: profile.Address ?? '',
      employmentStatus:
        typeof profile.EmploymentStatus === 'number'
          ? (profile.EmploymentStatus as number)
          : ('' as number | ''),
      employer: profile.Employer ?? '',
      jobTitle: profile.BusinessTitle ?? '',
      maritalStatus:
        typeof profile.MarriageStatus === 'number'
          ? (profile.MarriageStatus as number)
          : ('' as number | ''),
      spouseFirstName: profile.SpouseFirstName ?? '',
      spouseLastName: profile.SpouseLastName ?? '',
      spouseBirthday: profile.SpouseBirthday
        ? dayjs(profile.SpouseBirthday)
        : null,
    },
    onSubmit: () => undefined,
  });

  const [dependentsCount, setDependentsCount] = React.useState<number>(0);
  const [dependents, setDependents] = React.useState<UserDependentProfile[]>(
    [],
  );
  const [showDependents, setShowDependents] = React.useState<boolean>(false);

  React.useEffect(() => {
    const dependentList = profile.Dependents ?? [];
    setDependents(dependentList);
    setDependentsCount(dependentList.length);
    setShowDependents(dependentList.length > 0);
  }, [profile]);

  const dependentRows = React.useMemo(
    () => buildDependents(dependentsCount, dependents),
    [dependentsCount, dependents],
  );

  const handleDependentsCountChange = (value: string) => {
    const numeric = Number(value.replace(/[^0-9]/g, ''));
    if (Number.isNaN(numeric)) return;
    if (numeric <= 0) {
      setDependentsCount(0);
      setShowDependents(false);
      setDependents([]);
      return;
    }
    const clamped = Math.max(1, Math.min(20, numeric));
    setDependentsCount(clamped);
    setShowDependents(clamped > 0);
  };

  const handleDependentBirthdayChange = (
    index: number,
    value: Dayjs | null,
  ) => {
    setDependents((prev) => {
      const next = [...buildDependents(dependentsCount, prev)];
      next[index] = {
        ...next[index],
        BirthDay: toIsoMidnight(value),
      };
      return next;
    });
  };

  const formatCurrencyFromDigits = React.useCallback((digitString: string) => {
    const cleaned = digitString.replace(/\D/g, '');
    const cents = cleaned.length ? Number(cleaned) : 0;
    const amount = cents / 100;
    return formatNumberToCurrency(amount);
  }, []);

  const handleNetWorthChange: React.ChangeEventHandler<
    HTMLInputElement
  > = async (event) => {
    const raw = event.target.value;
    const digits = raw.replace(/\D/g, '');
    const formatted = formatCurrencyFromDigits(digits);
    if (!formik.touched.netWorth) {
      await formik.setFieldTouched('netWorth', true, false);
    }
    await formik.setFieldValue('netWorth', formatted, true);
  };

  const handlePhoneChange: React.ChangeEventHandler<HTMLInputElement> = async (
    event,
  ) => {
    const digits = digitsOnly(event.target.value).slice(0, 10);
    if (!formik.touched.phoneNumber) {
      formik.setFieldTouched('phoneNumber', true, false);
    }
    await formik.setFieldValue('phoneNumber', digits, true);
  };

  const allDependentsValid =
    !showDependents || dependentsCount === 0
      ? true
      : dependentRows
          .slice(0, dependentsCount)
          .every((d) => Boolean(d.BirthDay));

  const initialDependents = (profile.Dependents ??
    []) as UserDependentProfile[];
  const normalizeToMDY = (value?: string | null): string =>
    value ? dayjs(value).format('M/D/YYYY') : '';

  const dependentsChanged =
    dependentsCount !== initialDependents.length ||
    dependentRows.slice(0, dependentsCount).some((dependent, index) => {
      const current = normalizeToMDY(dependent?.BirthDay ?? null);
      const initial = normalizeToMDY(
        initialDependents[index]?.BirthDay ?? null,
      );
      return current !== initial;
    });

  const hasFormChanges = formik.dirty;
  const canSubmit =
    formik.isValid &&
    allDependentsValid &&
    (hasFormChanges || dependentsChanged);

  const handleSave = async () => {
    const errors = await formik.validateForm();
    const hasErrors = Object.keys(errors).length > 0;
    if (hasErrors || !allDependentsValid || !canSubmit) {
      formik.setTouched(
        Object.keys(formik.values).reduce(
          (acc, key) => ({ ...acc, [key]: true }),
          {} as Record<keyof UserProfileFormValues, boolean>,
        ),
        true,
      );
      return;
    }

    const formatted = (formik.values.address || '').trim();
    let derivedAddressLine1: string | null = null;
    let derivedCity: string | null = null;
    let derivedState: string | null = null;
    let derivedZip: string | null = null;
    if (formatted && formatted.split(',').length > 2) {
      const parts = formatted
        .split(',')
        .map((x) => x.trim())
        .reverse();
      if (/^usa$/i.test(parts[0] ?? '')) {
        parts.splice(0, 1);
      }
      const stateAndZip = parts[0] ?? '';
      derivedCity = parts[1] ?? null;
      derivedAddressLine1 = parts[2] ?? null;
      if (stateAndZip) {
        const sz = stateAndZip.split(' ').filter(Boolean);
        derivedState = sz[0] ?? null;
        derivedZip = sz.length > 1 ? (sz[1] ?? null) : null;
      }
    }

    const dependentsPayload =
      dependentsCount > 0
        ? dependentRows.slice(0, dependentsCount).map((dep, index) => ({
            UserDependentId:
              dep.UserDependentId ??
              initialDependents[index]?.UserDependentId ??
              null,
            BirthDay: toIsoMidnight(dep.BirthDay ?? null),
          }))
        : [];

    const payload: Partial<UserProfile> = {
      FirstName: profile.CanEditNameAndEmail
        ? formik.values.firstName
        : profile.FirstName,
      LastName: profile.CanEditNameAndEmail
        ? formik.values.lastName
        : profile.LastName,
      EmailAddress: profile.CanEditNameAndEmail
        ? formik.values.email
        : profile.EmailAddress,
      UserProfileId: profile.UserProfileId,
      UserId: profile.UserId,
      CanEditNameAndEmail: profile.CanEditNameAndEmail,
      Birthday: toIsoMidnight(formik.values.birthday),
      Gender: toNumberOrNull(formik.values.gender),
      ApproximateNetWorth: sanitizeCurrencyInput(formik.values.netWorth),
      PhoneNumber: formik.values.phoneNumber || null,
      Address: formik.values.address || null,
      AddressLine1: derivedAddressLine1,
      City: derivedCity,
      State: derivedState,
      Zip: derivedZip,
      EmploymentStatus: toNumberOrNull(formik.values.employmentStatus),
      Employer: formik.values.employer || null,
      BusinessTitle: formik.values.jobTitle || null,
      EmploymentStatusDisplay: profile.EmploymentStatusDisplay ?? '',
      MarriageStatus: toNumberOrNull(formik.values.maritalStatus),
      MarriageStatusDisplay: profile.MarriageStatusDisplay ?? '',
      SpouseFirstName: formik.values.spouseFirstName || null,
      SpouseLastName: formik.values.spouseLastName || null,
      SpouseBirthday: toIsoMidnight(formik.values.spouseBirthday),
      Dependents: dependentsPayload,
    };

    await onSubmit(payload);
  };

  return {
    formik,
    dependentsCount,
    dependentRows,
    showDependents,
    handleDependentsCountChange,
    handleDependentBirthdayChange,
    handleNetWorthChange,
    handlePhoneChange,
    handleSave,
    canSubmit,
  };
};

interface UserProfileFormFieldsProps {
  controller: UserProfileFormController;
  profile: UserProfile;
}

export const UserProfileFormFields: React.FC<UserProfileFormFieldsProps> = ({
  controller,
  profile,
}) => {
  const { t } = useLocalization();
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down('md'));
  const { formik } = controller;

  return (
    <Stack spacing={4}>
      <section>
        <Typography
          variant='subtitle1'
          sx={{ mb: 2 }}
        >
          {t('modal.sections.personalInformation', 'Personal Information')}
        </Typography>
        <Grid
          container
          spacing={isSmall ? 2 : 3}
        >
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              value={formik.values.firstName}
              name='firstName'
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              label={t('firstName', 'First Name')}
              required={Boolean(profile?.CanEditNameAndEmail)}
              fullWidth
              variant='outlined'
              disabled={!profile?.CanEditNameAndEmail}
              error={
                formik.touched.firstName && Boolean(formik.errors.firstName)
              }
              helperText={
                formik.touched.firstName &&
                typeof formik.errors.firstName === 'string'
                  ? formik.errors.firstName
                  : undefined
              }
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              value={formik.values.lastName}
              name='lastName'
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              label={t('lastName', 'Last Name')}
              required={Boolean(profile?.CanEditNameAndEmail)}
              fullWidth
              variant='outlined'
              disabled={!profile?.CanEditNameAndEmail}
              error={formik.touched.lastName && Boolean(formik.errors.lastName)}
              helperText={
                formik.touched.lastName &&
                typeof formik.errors.lastName === 'string'
                  ? formik.errors.lastName
                  : undefined
              }
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <DatePicker
              label={t('dateOfBirth', 'Date of Birth')}
              value={formik.values.birthday}
              onChange={async (value) =>
                await formik.setFieldValue('birthday', value, true)
              }
              maxDate={dayjs()}
              enableAccessibleFieldDOMStructure={!isCapacitorApp()}
              slotProps={{
                textField: {
                  fullWidth: true,
                  name: 'birthday',
                  InputLabelProps: { shrink: true },
                  sx: outlinedInputRootSx,
                  inputProps: isCapacitorApp() ? { readOnly: true } : undefined,
                  error:
                    formik.touched.birthday && Boolean(formik.errors.birthday),
                  helperText:
                    formik.touched.birthday &&
                    typeof formik.errors.birthday === 'string'
                      ? formik.errors.birthday
                      : undefined,
                },
              }}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              select
              fullWidth
              name='gender'
              value={
                formik.values.gender === '' ? '' : Number(formik.values.gender)
              }
              onChange={async (event) => {
                const raw = event.target.value as string | number;
                const str = String(raw);
                await formik.setFieldValue(
                  'gender',
                  str === '' ? '' : Number(str),
                  true,
                );
              }}
              label={t('forms.gender.label', 'Gender')}
              slotProps={{
                inputLabel: {
                  shrink: true,
                },
                select: {
                  displayEmpty: true,
                  renderValue: (selected) => {
                    const raw = selected as unknown as string | number;
                    if (raw === '' || raw === undefined) {
                      return (
                        <Typography color='text.secondary'>
                          {t('forms.gender.select', 'Select gender')}
                        </Typography>
                      );
                    }
                    const id = Number(raw);
                    const option = GENDER_OPTIONS.find(
                      (item) => item.value === id,
                    );
                    return option ? t(option.labelKey, option.labelKey) : '';
                  },
                },
              }}
              sx={outlinedInputRootSx}
            >
              <MenuItem value=''>
                {t('forms.gender.select', 'Select gender')}
              </MenuItem>
              {GENDER_OPTIONS.map((option) => (
                <MenuItem
                  key={option.value}
                  value={option.value}
                >
                  {t(option.labelKey, option.labelKey)}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              label={t('approximateNetWorth', 'Approximate Net Worth')}
              name='netWorth'
              value={formik.values.netWorth}
              onChange={controller.handleNetWorthChange}
              onBlur={formik.handleBlur}
              fullWidth
              slotProps={{
                inputLabel: {
                  shrink: true,
                },
                htmlInput: { inputMode: 'numeric', pattern: '[0-9]*' },
              }}
              sx={outlinedInputRootSx}
              error={formik.touched.netWorth && Boolean(formik.errors.netWorth)}
              helperText={
                formik.touched.netWorth &&
                typeof formik.errors.netWorth === 'string'
                  ? formik.errors.netWorth
                  : undefined
              }
            />
          </Grid>
        </Grid>
      </section>

      <Divider />

      <section>
        <Typography
          variant='subtitle1'
          sx={{ mb: 2 }}
        >
          {t('modal.sections.contactInformation', 'Contact Information')}
        </Typography>
        <Grid
          container
          spacing={isSmall ? 2 : 3}
        >
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              value={formik.values.email}
              name='email'
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              label={t('email', 'Email')}
              required={Boolean(profile?.CanEditNameAndEmail)}
              fullWidth
              variant='outlined'
              disabled={!profile?.CanEditNameAndEmail}
              error={formik.touched.email && Boolean(formik.errors.email)}
              helperText={
                formik.touched.email && typeof formik.errors.email === 'string'
                  ? formik.errors.email
                  : undefined
              }
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              label={t('primaryPhoneNumber', 'Primary Phone Number')}
              name='phoneNumber'
              value={formatPhoneNumber(formik.values.phoneNumber)}
              onChange={controller.handlePhoneChange}
              onBlur={formik.handleBlur}
              fullWidth
              slotProps={{
                inputLabel: {
                  shrink: true,
                },
                htmlInput: {
                  inputMode: 'numeric',
                  pattern: '[0-9]*',
                  maxLength: 14,
                },
              }}
              sx={outlinedInputRootSx}
              error={
                formik.touched.phoneNumber && Boolean(formik.errors.phoneNumber)
              }
              helperText={
                formik.touched.phoneNumber &&
                typeof formik.errors.phoneNumber === 'string'
                  ? formik.errors.phoneNumber
                  : undefined
              }
            />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <AddressAutocomplete
              label={t('address', 'Address')}
              name='address'
              value={formik.values.address}
              onChange={async (value) =>
                await formik.setFieldValue('address', value, true)
              }
              onBlur={formik.handleBlur as unknown as () => void}
            />
          </Grid>
        </Grid>
      </section>

      <Divider />

      <section>
        <Typography
          variant='subtitle1'
          sx={{ mb: 2 }}
        >
          {t('modal.sections.employmentInformation', 'Employment Information')}
        </Typography>
        <Grid
          container
          spacing={isSmall ? 2 : 3}
        >
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              select
              fullWidth
              name='employmentStatus'
              value={
                formik.values.employmentStatus === ''
                  ? ''
                  : Number(formik.values.employmentStatus)
              }
              onChange={async (event) => {
                const raw = event.target.value as string | number;
                const str = String(raw);
                await formik.setFieldValue(
                  'employmentStatus',
                  str === '' ? '' : Number(str),
                  true,
                );
              }}
              label={t('forms.employmentStatus.label', 'Employment Status')}
              slotProps={{
                inputLabel: {
                  shrink: true,
                },
                select: {
                  displayEmpty: true,
                  renderValue: (selected) => {
                    const raw = selected as unknown as string | number;
                    if (raw === '' || raw === undefined) {
                      return (
                        <Typography color='text.secondary'>
                          {t(
                            'forms.employmentStatus.select',
                            'Select Employment Status',
                          )}
                        </Typography>
                      );
                    }
                    const id = Number(raw);
                    const option = EMPLOYMENT_STATUS_OPTIONS.find(
                      (item) => item.value === id,
                    );
                    return option ? t(option.labelKey, option.labelKey) : '';
                  },
                },
              }}
              sx={outlinedInputRootSx}
            >
              <MenuItem value=''>
                {t('forms.employmentStatus.select', 'Select Employment Status')}
              </MenuItem>
              {EMPLOYMENT_STATUS_OPTIONS.map((option) => (
                <MenuItem
                  key={option.value}
                  value={option.value}
                >
                  {t(option.labelKey, option.labelKey)}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              label={t('employer', 'Employer')}
              name='employer'
              value={formik.values.employer}
              onChange={formik.handleChange}
              fullWidth
              slotProps={{
                inputLabel: { shrink: true },
              }}
              sx={outlinedInputRootSx}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              label={t('jobTitle', 'Job Title')}
              name='jobTitle'
              value={formik.values.jobTitle}
              onChange={formik.handleChange}
              fullWidth
              slotProps={{
                inputLabel: { shrink: true },
              }}
              sx={outlinedInputRootSx}
            />
          </Grid>
        </Grid>
      </section>

      <Divider />

      <section>
        <Typography
          variant='subtitle1'
          sx={{ mb: 2 }}
        >
          {t('modal.sections.maritalStatus', 'Marital Status')}
        </Typography>
        <Grid
          container
          spacing={isSmall ? 2 : 3}
        >
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              select
              fullWidth
              name='maritalStatus'
              value={
                formik.values.maritalStatus === ''
                  ? ''
                  : Number(formik.values.maritalStatus)
              }
              onChange={async (event) => {
                const raw = event.target.value as string | number;
                const str = String(raw);
                await formik.setFieldValue(
                  'maritalStatus',
                  str === '' ? '' : Number(str),
                  true,
                );
              }}
              label={t('forms.maritalStatus', 'Marital Status')}
              slotProps={{
                inputLabel: { shrink: true },
                select: {
                  displayEmpty: true,
                  renderValue: (selected) => {
                    const raw = selected as unknown as string | number;
                    if (raw === '' || raw === undefined) {
                      return (
                        <Typography color='text.secondary'>
                          {t(
                            'modal.labels.selectMaritalStatus',
                            'Select marital status',
                          )}
                        </Typography>
                      );
                    }
                    const id = Number(raw);
                    const option = MARITAL_STATUS_OPTIONS.find(
                      (item) => item.value === id,
                    );
                    return option ? t(option.labelKey, option.labelKey) : '';
                  },
                },
              }}
              sx={outlinedInputRootSx}
            >
              <MenuItem value=''>
                {t('modal.labels.selectMaritalStatus', 'Select marital status')}
              </MenuItem>
              {MARITAL_STATUS_OPTIONS.map((option) => (
                <MenuItem
                  key={option.value}
                  value={option.value}
                >
                  {t(option.labelKey, option.labelKey)}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              label={t('spouseFirstName', 'Spouse/Partner First Name')}
              name='spouseFirstName'
              value={formik.values.spouseFirstName}
              onChange={formik.handleChange}
              fullWidth
              slotProps={{
                inputLabel: { shrink: true },
              }}
              sx={outlinedInputRootSx}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              label={t('spouseLastName', 'Spouse/Partner Last Name')}
              name='spouseLastName'
              value={formik.values.spouseLastName}
              onChange={formik.handleChange}
              fullWidth
              slotProps={{
                inputLabel: { shrink: true },
              }}
              sx={outlinedInputRootSx}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <DatePicker
              label={t('spouseBirthday', 'Spouse/Partner Birthday')}
              value={formik.values.spouseBirthday}
              onChange={async (value) =>
                await formik.setFieldValue('spouseBirthday', value, true)
              }
              maxDate={dayjs()}
              enableAccessibleFieldDOMStructure={!isCapacitorApp()}
              slotProps={{
                textField: {
                  fullWidth: true,
                  name: 'spouseBirthday',
                  InputLabelProps: { shrink: true },
                  sx: outlinedInputRootSx,
                  inputProps: isCapacitorApp() ? { readOnly: true } : undefined,
                  error:
                    formik.touched.spouseBirthday &&
                    Boolean(formik.errors.spouseBirthday),
                  helperText:
                    formik.touched.spouseBirthday &&
                    typeof formik.errors.spouseBirthday === 'string'
                      ? formik.errors.spouseBirthday
                      : undefined,
                },
              }}
            />
          </Grid>
        </Grid>
      </section>

      <Divider />

      <section>
        <Grid
          container
          spacing={isSmall ? 2 : 3}
        >
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              label={t(
                'modal.labels.numberOfDependents',
                'Number of Dependents',
              )}
              value={
                controller.dependentsCount
                  ? String(controller.dependentsCount)
                  : ''
              }
              onChange={(event) =>
                controller.handleDependentsCountChange(event.target.value)
              }
              fullWidth
              slotProps={{
                htmlInput: { inputMode: 'numeric' },
                inputLabel: { shrink: true },
              }}
              sx={outlinedInputRootSx}
            />
          </Grid>
        </Grid>
        {controller.showDependents && controller.dependentsCount > 0 ? (
          <Box sx={{ mt: 2 }}>
            <Grid
              container
              spacing={isSmall ? 2 : 3}
            >
              {controller.dependentRows.map((dependent, index) => (
                <Grid
                  size={{ xs: 12, md: 6 }}
                  key={dependent.UserDependentId ?? index}
                >
                  <DatePicker
                    label={t('modal.labels.dependentBirthday', {
                      num: index + 1,
                    })}
                    value={
                      dependent.BirthDay ? dayjs(dependent.BirthDay) : null
                    }
                    onChange={(value) =>
                      controller.handleDependentBirthdayChange(index, value)
                    }
                    maxDate={dayjs()}
                    enableAccessibleFieldDOMStructure={!isCapacitorApp()}
                    slotProps={
                      isCapacitorApp()
                        ? { textField: { inputProps: { readOnly: true } } }
                        : undefined
                    }
                  />
                </Grid>
              ))}
            </Grid>
          </Box>
        ) : null}
      </section>
    </Stack>
  );
};
